import { useParams, Link, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { blogPosts, type BlogPost } from "../../../mocks/blogPosts";
import { blogPostsEn } from "../../../mocks/blogPosts.en";
import InternalNavbar from "../../../components/feature/InternalNavbar";

function BodyBlock({ block }: { block: BlogPost["body"][number] }) {
  if (block.type === "heading") {
    return (
      <h2
        className="text-xl font-bold text-stone-900 mt-10 mb-3"
        style={{ fontFamily: "'Playfair Display', serif" }}
      >
        {block.content as string}
      </h2>
    );
  }
  if (block.type === "quote") {
    return (
      <blockquote className="border-l-4 border-amber-400 pl-5 py-2 my-6 bg-amber-50 rounded-r-xl">
        <p className="text-stone-700 text-sm italic leading-relaxed">{block.content as string}</p>
      </blockquote>
    );
  }
  if (block.type === "list") {
    return (
      <ul className="my-5 space-y-2 pl-1">
        {(block.content as string[]).map((item, i) => (
          <li key={i} className="flex items-start gap-3 text-stone-600 text-sm leading-relaxed">
            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" />
            {item}
          </li>
        ))}
      </ul>
    );
  }
  return (
    <p className="text-stone-600 text-sm leading-relaxed my-4">{block.content as string}</p>
  );
}

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const isEn = i18n.language === "en";

  // Find post index by searching both arrays (slug may belong to either language)
  const postIndexEs = blogPosts.findIndex((p) => p.slug === slug);
  const postIndexEn = blogPostsEn.findIndex((p) => p.slug === slug);
  const postIndex = postIndexEs >= 0 ? postIndexEs : postIndexEn;

  // Resolve post in active language
  const post = postIndex >= 0
    ? (isEn ? blogPostsEn[postIndex] : blogPosts[postIndex])
    : null;

  // When language toggles, redirect to the equivalent slug in the new language
  useEffect(() => {
    if (postIndex < 0) return;
    const expectedSlug = isEn ? blogPostsEn[postIndex].slug : blogPosts[postIndex].slug;
    if (slug !== expectedSlug) {
      navigate(`/blog/${expectedSlug}`, { replace: true });
    }
  }, [isEn, postIndex, slug, navigate]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [slug]);

  const navLinks = [
    { label: isEn ? "Blog" : "Blog", href: "/blog" },
    { label: isEn ? "About us" : "Sobre nosotros", href: "/sobre-nosotros" },
  ];

  if (!post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white gap-4">
        <p className="text-stone-500 text-sm">
          {isEn ? "Article not found." : "Artículo no encontrado."}
        </p>
        <Link to="/blog" className="text-amber-600 text-sm font-semibold hover:underline cursor-pointer">
          {isEn ? "Back to blog" : "Volver al blog"}
        </Link>
      </div>
    );
  }

  // Related posts from the correct language array
  const currentLangPosts = isEn ? blogPostsEn : blogPosts;
  const related = currentLangPosts
    .filter((p) => p.slug !== post.slug && p.category === post.category)
    .slice(0, 2);

  return (
    <div className="min-h-screen bg-white flex flex-col" style={{ fontFamily: "'Inter', sans-serif" }}>
      <InternalNavbar
        links={navLinks}
        rightCta={{ label: isEn ? "See plans" : "Ver planes", href: "/#precios" }}
      />

      <main className="flex-1">
        {/* Hero image */}
        <div className="w-full h-72 sm:h-96 overflow-hidden relative">
          <img
            src={post.img}
            alt={post.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
        </div>

        {/* Article container */}
        <div className="max-w-2xl mx-auto px-6 py-10">
          {/* Back */}
          <button
            onClick={() => navigate("/blog")}
            className="flex items-center gap-1.5 text-stone-400 hover:text-stone-700 text-xs font-medium mb-8 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line text-sm" />
            {isEn ? "Back to blog" : "Volver al blog"}
          </button>

          {/* Meta */}
          <div className="flex items-center gap-2 mb-4">
            <span className="px-2.5 py-0.5 bg-amber-100 text-amber-700 text-xs font-bold rounded-full">
              {post.category}
            </span>
            {post.featured && (
              <span className="text-stone-400 text-xs">
                {isEn ? "Featured" : "Destacado"}
              </span>
            )}
          </div>

          <h1
            className="text-3xl font-bold text-stone-900 leading-tight mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {post.title}
          </h1>

          <div className="flex items-center gap-4 text-xs text-stone-400 mb-8 pb-8 border-b border-stone-100">
            <span className="flex items-center gap-1">
              <i className="ri-calendar-line" />
              {post.date}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-time-line" />
              {post.readTime} {isEn ? "read" : "lectura"}
            </span>
          </div>

          {/* Body */}
          <div>
            {post.body.map((block, i) => (
              <BodyBlock key={i} block={block} />
            ))}
          </div>

          {/* CTA */}
          <div className="mt-14 p-8 bg-stone-900 rounded-2xl text-center">
            <h3 className="text-white font-bold text-lg mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              {isEn ? "Ready to try it in your business?" : "¿Listo para probarlo en tu negocio?"}
            </h3>
            <p className="text-stone-400 text-sm mb-5">
              {isEn
                ? "Activate Auria in under 30 minutes and stop missing calls starting today."
                : "Activa Auria en menos de 30 minutos y deja de perder llamadas desde hoy."}
            </p>
            <Link
              to="/#precios"
              className="inline-block px-6 py-2.5 bg-amber-500 hover:bg-amber-400 text-white text-sm font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
            >
              {isEn ? "See plans & pricing" : "Ver planes y precios"}
            </Link>
          </div>

          {/* Related articles */}
          {related.length > 0 && (
            <div className="mt-14">
              <h4 className="text-sm font-bold text-stone-900 uppercase tracking-widest mb-6">
                {isEn ? "You might also like" : "También te puede interesar"}
              </h4>
              <div className="grid sm:grid-cols-2 gap-5">
                {related.map((r) => (
                  <Link
                    key={r.slug}
                    to={`/blog/${r.slug}`}
                    className="group block rounded-xl overflow-hidden border border-stone-100 hover:border-amber-200 transition-colors cursor-pointer"
                  >
                    <div className="h-36 overflow-hidden">
                      <img
                        src={r.img}
                        alt={r.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <span className="text-xs font-bold text-amber-600 mb-1 block">{r.category}</span>
                      <p className="text-stone-900 font-semibold text-sm leading-snug group-hover:text-amber-600 transition-colors">
                        {r.title}
                      </p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-stone-50 border-t border-stone-100 px-6 py-6 mt-10">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-stone-400 text-xs">
            {isEn ? "© 2026 Auria. All rights reserved." : "© 2026 Auria. Todos los derechos reservados."}
          </p>
          <div className="flex items-center gap-4 text-xs">
            <Link to="/" className="text-stone-400 hover:text-stone-600 transition-colors cursor-pointer">
              {isEn ? "Home" : "Inicio"}
            </Link>
            <Link to="/blog" className="text-stone-400 hover:text-stone-600 transition-colors cursor-pointer">Blog</Link>
            <Link to="/sobre-nosotros" className="text-stone-400 hover:text-stone-600 transition-colors cursor-pointer">
              {isEn ? "About" : "Sobre nosotros"}
            </Link>
            <Link to="/privacidad" className="text-stone-400 hover:text-stone-600 transition-colors cursor-pointer">
              {isEn ? "Privacy" : "Privacidad"}
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
